import React, { useState, useEffect } from 'react';
import axios from 'axios';

export const EventCreationForm = ({ matchId, clock, onEventCreated }) => {
  const [eventTypes, setEventTypes] = useState([]);
  const [lineup, setLineup] = useState([]);
  const [match, setMatch] = useState(null);

  // Form state - initialize from clock
  const [eventType, setEventType] = useState('');
  const [teamId, setTeamId] = useState('');
  const [playerId, setPlayerId] = useState('');
  const [secondaryPlayerId, setSecondaryPlayerId] = useState('');
  const [minute, setMinute] = useState(clock?.minute || 0);
  const [second, setSecond] = useState(clock?.second || 0);
  const [half, setHalf] = useState(clock?.half || 1);

  // Captain transfer state
  const [captainTeamId, setCaptainTeamId] = useState('');
  const [newCaptainId, setNewCaptainId] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Update time when clock changes
  useEffect(() => {
    if (clock) {
      setMinute(clock.minute);
      setSecond(clock.second);
      setHalf(clock.half);
    }
  }, [clock]);

  useEffect(() => {
    loadData();
  }, [matchId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [typesRes, lineupRes, matchRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/event-types`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${matchId}/with-status`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/matches/${matchId}`, { headers })
      ]);

      setEventTypes(typesRes.data.data || []);
      setLineup(lineupRes.data || []);
      setMatch(matchRes.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const getTeamPlayers = (teamId) => {
    return lineup.filter(p => p.team_id === parseInt(teamId));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const eventData = {
      matchId: parseInt(matchId),
      eventTypeId: parseInt(eventType), // Changed from eventType
      teamId: parseInt(teamId),
      playerId: playerId ? parseInt(playerId) : null,
      secondaryPlayerId: secondaryPlayerId ? parseInt(secondaryPlayerId) : null,
      minute: parseInt(minute),
      second: parseInt(second),
      half: parseInt(half)
    };

    console.log('Creating event:', eventData);
    onEventCreated(eventData);
    resetForm();
  };

  const resetForm = () => {
    setEventType('');
    setTeamId('');
    setPlayerId('');
    setSecondaryPlayerId('');
    setMinute(0);
    setSecond(0);
  };

  const handleCaptainTransfer = async () => {
    if (!newCaptainId) return;
    setTransferring(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${matchId}/transfer-captain`,
        { newCaptainId: parseInt(newCaptainId) },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCaptainTeamId('');
      setNewCaptainId('');
      loadData(); // Refresh lineup
    } catch (error) {
      console.error('Error transferring captaincy:', error);
      alert(error.response?.data?.error || 'Failed to transfer captaincy');
    } finally {
      setTransferring(false);
    }
  };

  if (!match) return null;

  const teamPlayers = teamId ? getTeamPlayers(teamId) : [];
  const captainTeamPlayers = captainTeamId ? getTeamPlayers(captainTeamId) : [];
  const currentCaptain = captainTeamPlayers.find(p => p.is_captain);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-xl font-semibold mb-4">Create Event</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Event Type */}
        <div>
          <label className="block text-sm font-medium mb-1">Event Type</label>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="w-full border rounded px-3 py-2"
            required
          >
            <option value="">Select event type</option>
            {eventTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>

        {/* Team */}
        <div>
          <label className="block text-sm font-medium mb-1">Team</label>
          <select
            value={teamId}
            onChange={(e) => {
              setTeamId(e.target.value);
              setPlayerId('');
              setSecondaryPlayerId('');
            }}
            className="w-full border rounded px-3 py-2"
            required
          >
            <option value="">Select team</option>
            <option value={match.home_team_id}>{match.home_team?.name}</option>
            <option value={match.away_team_id}>{match.away_team?.name}</option>
          </select>
        </div>

        {/* Player */}
        <div>
          <label className="block text-sm font-medium mb-1">Player</label>
          <select
            value={playerId}
            onChange={(e) => setPlayerId(e.target.value)}
            className="w-full border rounded px-3 py-2"
            disabled={!teamId}
          >
            <option value="">Select player (optional)</option>
            {teamPlayers.map(p => (
              <option key={p.id} value={p.player_id}>
                #{p.jersey_number} - {p.player.first_name} {p.player.last_name}
              </option>
            ))}
          </select>
        </div>

        {/* Secondary Player (for assists, substitutions) */}
        <div>
          <label className="block text-sm font-medium mb-1">Secondary Player (optional)</label>
          <select
            value={secondaryPlayerId}
            onChange={(e) => setSecondaryPlayerId(e.target.value)}
            className="w-full border rounded px-3 py-2"
            disabled={!teamId}
          >
            <option value="">Select player (optional)</option>
            {teamPlayers.map(p => (
              <option key={p.id} value={p.player_id}>
                #{p.jersey_number} - {p.player.first_name} {p.player.last_name}
              </option>
            ))}
          </select>
        </div>

        {/* Time */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Minute</label>
            <input
              type="number"
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
              className="w-full border rounded px-3 py-2"
              min="0"
              max="120"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Second</label>
            <input
              type="number"
              value={second}
              onChange={(e) => setSecond(e.target.value)}
              className="w-full border rounded px-3 py-2"
              min="0"
              max="59"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Half</label>
            <select
              value={half}
              onChange={(e) => setHalf(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="1">1st Half</option>
              <option value="2">2nd Half</option>
              <option value="3">ET 1st</option>
              <option value="4">ET 2nd</option>
            </select>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
        >
          Create Event
        </button>
      </form>

      {/* Captain Transfer */}
      <div className="mt-6 pt-6 border-t">
        <h3 className="text-lg font-semibold mb-3">Captain Transfer</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Team</label>
            <select
              value={captainTeamId}
              onChange={(e) => { setCaptainTeamId(e.target.value); setNewCaptainId(''); }}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select team</option>
              <option value={match.home_team_id}>{match.home_team?.name}</option>
              <option value={match.away_team_id}>{match.away_team?.name}</option>
            </select>
          </div>

          {captainTeamId && currentCaptain && (
            <p className="text-sm text-gray-600">
              Current captain: <span className="font-semibold">#{currentCaptain.jersey_number} {currentCaptain.player.first_name} {currentCaptain.player.last_name}</span>
            </p>
          )}

          {captainTeamId && (
            <div>
              <label className="block text-sm font-medium mb-1">New Captain</label>
              <select
                value={newCaptainId}
                onChange={(e) => setNewCaptainId(e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">Select new captain</option>
                {captainTeamPlayers
                  .filter(p => !p.is_captain && p.eligibility?.eligible)
                  .map(p => (
                    <option key={p.id} value={p.id}>
                      #{p.jersey_number} - {p.player.first_name} {p.player.last_name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {newCaptainId && (
            <button
              onClick={handleCaptainTransfer}
              disabled={transferring}
              className="w-full px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 font-semibold"
            >
              {transferring ? 'Transferring...' : 'Transfer Captaincy'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
